// Audience Model Type (Only Relevant Fields)
export type Audience = {
  id: string;
  name: string;
  recordsIncluded: number;
  purchasedAt: Date;
  updatedAt: Date;
};

// Destination Partner Model Type
// - `name` is the brand label (e.g. "LiveRamp"). For LiveRamp folder
//   variants every tile shares the same name.
// - `displayName` is the per-variant label shown in place of "Connected" in
//   the Activations tile (e.g. "AIQ - Custom - IND - SR - RE - B2B").
// - `filenameSuffix` is the folder NUMBER ("1"–"5") appended to the
//   audience name when the file is delivered to AUDIENCE_DELIVERY_BUCKET.
//   So variant #2 produces `LR_<audienceName>_2.csv`. Its presence also
//   marks this partner as an internal-delivery variant, so the
//   ConfigureDistribution step can skip the S3/SFTP credentials form.
export type DestinationPartner = {
  id: string;
  name: string;
  slug: string;
  displayName?: string | null;
  filenameSuffix?: string | null;
};

export type RecentActivation = {
  id: string;
  audienceName: string;
  recordsIncluded: number;
  startedAt: Date;
  destinationPartner: DestinationPartner;
};

// Reducer State
export interface ActivationState {
  selectedAudience: Audience | null; // Only one audience can be selected
  selectedDestinations: DestinationPartner[];
  audienceDistribution: Record<string, number>; // { "meta": 0.75, "google-ads": 0.5 }
  stage: 'selecting' | 'ready';
  recentActivations: RecentActivation[];
}

export const initialState: ActivationState = {
  selectedAudience: null,
  selectedDestinations: [],
  audienceDistribution: {},
  stage: 'selecting',
  recentActivations: [],
};

// Action Types
export enum ActivationActionTypes {
  RESET = 'reset',
  SET_AUDIENCE = 'set-audience',
  TOGGLE_DESTINATION = 'toggle-destination',
  SET_DISTRIBUTION_PERCENTAGE = 'set-distribution-percentage',
  SET_STAGE = 'set-stage',
  SAVE_ACTIVATIONS = 'save-activations',
}

// Action Types
export type ActivationAction =
  | { type: ActivationActionTypes.RESET; payload?: undefined }
  | { type: ActivationActionTypes.SET_AUDIENCE; payload: Audience }
  | {
      type: ActivationActionTypes.TOGGLE_DESTINATION;
      payload: DestinationPartner;
    }
  | {
      type: ActivationActionTypes.SET_DISTRIBUTION_PERCENTAGE;
      payload: { id: string; percentage: number };
    }
  | { type: ActivationActionTypes.SET_STAGE; payload?: undefined }
  | {
      type: ActivationActionTypes.SAVE_ACTIVATIONS;
      payload: RecentActivation[];
    };

// Reducer Function
// Reducer Function
export const reducer = (
  state: ActivationState,
  action: ActivationAction
): ActivationState => {
  const { type, payload } = action;

  switch (type) {
    case ActivationActionTypes.SET_AUDIENCE:
      return {
        ...state,
        selectedAudience: payload, // Only one audience can be selected
      };

    case ActivationActionTypes.TOGGLE_DESTINATION: {
      const destinationExists = state.selectedDestinations.some(
        (dest) => dest.id === action.payload.id
      );

      const updatedDestinations = destinationExists
        ? state.selectedDestinations.filter(
            (dest) => dest.id !== action.payload.id
          ) // Remove if already exists
        : [...state.selectedDestinations, action.payload]; // Add new destination

      const updatedAudienceDistribution = destinationExists
        ? Object.fromEntries(
            Object.entries(state.audienceDistribution).filter(
              ([key]) => key !== action.payload.id
            )
          ) // Remove allocation
        : { ...state.audienceDistribution, [action.payload.id]: 1 }; // Default to 100%

      return {
        ...state,
        selectedDestinations: updatedDestinations,
        audienceDistribution: updatedAudienceDistribution,
      };
    }

    case ActivationActionTypes.SET_DISTRIBUTION_PERCENTAGE:
      return {
        ...state,
        audienceDistribution: {
          ...state.audienceDistribution,
          [payload.id]: payload.percentage,
        },
      };

    case ActivationActionTypes.SAVE_ACTIVATIONS:
      return { ...state, recentActivations: action.payload };

    case ActivationActionTypes.RESET:
      return { ...initialState };

    case ActivationActionTypes.SET_STAGE:
      return {
        ...state,
        stage:
          state.selectedAudience && state.selectedDestinations.length > 0
            ? 'ready'
            : 'selecting',
      };

    default:
      return state;
  }
};
